import { ReadingDirection } from '../../../../enums';
import { Config } from '../../../../video-data/config';
import { rgbArray2String, string2rgbaArray } from '../../../../libs/videoData';
import type { RgbaString } from '../../../../types';
import { getGlobalFontManager } from '../../../FontManager';
import { CanvasRenderer } from './BaseTextRenderer';
import type { TextDynamicAnimationRendererSettings } from './TextDynamicAnimationBase';
import type { TextDynamicGraphicRendererSettings } from './TextDynamicGraphicRenderer';
import { TextDynamicCircleRenderer } from './TextDynamicCircleRenderer';
import { TextDynamicBoxRenderer } from './TextDynamicBoxRenderer';
import { TextStyleDynamicAnimationRenderer } from './TextStyleDynamicAnimationRenderer';
import { TextDynamicBoxBorderRenderer } from './TextDynamicBoxBorderRenderer';

export interface TextStyleRendererSettings {
    animationConfigurations: TextDynamicAnimationRendererSettings[];
    fill: RgbaString;
    fontSize: number;
    fontFamily: string;
    lineHeight: number;
    readingDirection: ReadingDirection;
    renderHighlighted: boolean;
    renderHighlightedColor: boolean;
    shadow: boolean;
    shadowAngle: number;
    shadowBlur: number;
    shadowColor: RgbaString;
    shadowDistance: number;
    graphics: TextDynamicGraphicRendererSettings[];
}

type GraphicRenderers =
    | TextDynamicCircleRenderer
    | TextDynamicBoxRenderer
    | TextDynamicBoxBorderRenderer;

/**
 * Represents the decoration a words.
 */
export class TextStyleRenderer {
    fill: RgbaString = `rgba(0, 0, 0, 1)`;
    fontSize: number = 26;
    fontFamily: string = 'Arial';
    // TODO: we can probably move lineHeight up since we can only have one value per text block. In our current system
    //  we have one value and then copy it to both the normal and highlight style.
    lineHeight: number = 1.2;
    readingDirection: ReadingDirection = ReadingDirection.LTR;

    shadow: boolean = false;
    shadowAngle: number = 0;
    shadowBlur: number = 0;
    shadowColor: RgbaString = `rgba(0, 0, 0, 1)`;
    shadowDistance: number = 0;

    graphics: GraphicRenderers[] = [];

    animationConfigurations: TextStyleDynamicAnimationRenderer[] = [];
    // Specifies if this style should render the text within the highlighted areas
    renderHighlighted = true;
    /**
     * Hint to the Text that if true, render the highlighted elements with the highlighted color instead of the normal color.
     */
    renderHighlightedColor = false;

    addAnimationConfiguration(animationConfiguration: TextStyleDynamicAnimationRenderer) {
        this.animationConfigurations.push(animationConfiguration);
    }

    get colorGraphics(): GraphicRenderers[] {
        return this.graphics.filter((graphic) => !graphic.asMask);
    }

    get alphaGraphics(): GraphicRenderers[] {
        return this.graphics.filter((graphic) => graphic.asMask);
    }

    addColorGraphic(colorGraphic: GraphicRenderers) {
        colorGraphic.asMask = false;
        this.graphics.push(colorGraphic);
    }

    addAlphaGraphic(alphaGraphic: GraphicRenderers) {
        alphaGraphic.asMask = true;
        this.graphics.push(alphaGraphic);
    }

    apply(settings: TextStyleRendererSettings) {
        this.fill = settings.fill;
        this.fontSize = settings.fontSize;
        this.fontFamily = settings.fontFamily ?? this.fontFamily;
        this.lineHeight = settings.lineHeight;
        this.readingDirection = settings.readingDirection;
        this.renderHighlighted = settings.renderHighlighted;
        this.renderHighlightedColor = settings.renderHighlightedColor;
        this.shadow = settings.shadow;
        this.shadowAngle = settings.shadowAngle;
        this.shadowBlur = settings.shadowBlur;
        this.shadowColor = settings.shadowColor;
        this.shadowDistance = settings.shadowDistance;

        this.graphics = [];
        settings.graphics.forEach((graphic) => {
            const newGraphic = this.#createGraphic(graphic);
            this.graphics.push(newGraphic);
        });

        this.animationConfigurations = [];
        settings.animationConfigurations.forEach((animationConfig) => {
            const newAnimationConfiguration = new TextStyleDynamicAnimationRenderer();
            newAnimationConfiguration.apply(animationConfig);
            this.addAnimationConfiguration(newAnimationConfiguration);
        });

        return this;
    }

    clone() {
        const clone = new TextStyleRenderer();
        clone.apply(this);
        return clone;
    }

    /* The list of properties that can be animated during draw */
    static get animatableProperties() {
        return CanvasRenderer.animatableProperties;
    }

    static get animatableGeometricProperties() {
        return CanvasRenderer.animatableGeometricProperties;
    }

    /* Set up the context with the current style for rendering text. */
    setupContextWithStyle(
        context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
        alpha: number,
    ) {
        const fontManager = getGlobalFontManager();
        const fontsToUse = [this.fontFamily, fontManager.getLoadedFontFamilyNamesCssString()];
        const [r, g, b] = string2rgbaArray(this.fill);

        context.font = `${this.fontSize}px ${fontsToUse}`;
        context.fillStyle = rgbArray2String([r, g, b, alpha]);

        if (this.shadow) {
            const shadowOffsetX = Math.cos(this.shadowAngle) * this.shadowDistance;
            const shadowOffsetY = Math.sin(this.shadowAngle) * this.shadowDistance;
            context.shadowBlur = this.shadowBlur;
            context.shadowColor = this.shadowColor;
            context.shadowOffsetX = shadowOffsetX;
            context.shadowOffsetY = shadowOffsetY;
        }
    }

    /** Offset we need to apply to the baseline to match the requested line height **/
    get baselineOffset(): number {
        return this.fontSize * this.lineHeight;
    }

    #createGraphic(settings: TextDynamicGraphicRendererSettings): GraphicRenderers {
        let graphic: GraphicRenderers;
        switch (settings.type) {
            case Config.BOX_BORDER_GRAPHIC:
                graphic = new TextDynamicBoxBorderRenderer();
                graphic.apply(settings);
                break;
            case Config.BOX_GRAPHIC:
                graphic = new TextDynamicBoxRenderer();
                graphic.apply(settings);
                break;
            case Config.CIRCLE_GRAPHIC:
                graphic = new TextDynamicCircleRenderer();
                graphic.apply(settings);
                break;
            default:
                throw new Error(`Unknown graphic type: ${settings['type']}`);
        }
        return graphic;
    }
}
